import React, { useEffect, useState } from 'react';
import { createProduct, deleteProduct, getProducts, updateProduct } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../utils/apiError';
import { Package, Pencil, Plus, Trash2 } from 'lucide-react';

export default function Products() {
  const [products, setProducts] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    sale_price: '',
    cost_price: '',
    stock: '0',
    min_stock: '',
    category: '',
    image_path: '',
    is_active: true,
  });

  const fetchProducts = async () => {
    try {
      const response = await getProducts(true);
      const list = Array.isArray(response.data?.data) ? response.data.data : (Array.isArray(response.data) ? response.data : []);
      setProducts(list);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al cargar productos'));
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const openDrawer = (product = null) => {
    setSelectedProduct(product);
    if (product) {
      setFormData({
        name: product.name || '',
        description: product.description || '',
        sku: product.sku || '',
        sale_price: String(product.sale_price ?? ''),
        cost_price: product.cost_price != null ? String(product.cost_price) : '',
        stock: String(product.stock ?? 0),
        min_stock: product.min_stock != null ? String(product.min_stock) : '',
        category: product.category || '',
        is_active: Boolean(product.is_active),
      });
      setImageFile(null);
    } else {
      setFormData({
        name: '',
        description: '',
        sku: '',
        sale_price: '',
        cost_price: '',
        stock: '0',
        min_stock: '',
        category: '',
        is_active: true,
      });
      setImageFile(null);
    }
    setDrawerOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload = new FormData();
      payload.append('name', formData.name.trim());
      payload.append('description', formData.description.trim() || '');
      payload.append('sku', formData.sku.trim() || '');
      payload.append('sale_price', String(Number(formData.sale_price || 0)));
      payload.append('cost_price', formData.cost_price === '' ? '' : String(Number(formData.cost_price)));
      payload.append('stock', String(Number(formData.stock || 0)));
      payload.append('min_stock', formData.min_stock === '' ? '' : String(Number(formData.min_stock)));
      payload.append('category', formData.category.trim() || '');
      payload.append('is_active', formData.is_active ? '1' : '0');
      if (imageFile) {
        payload.append('image', imageFile);
      }

      if (selectedProduct) {
        payload.append('_method', 'PUT');
        await updateProduct(selectedProduct.id, payload);
        toast.success('Producto actualizado');
      } else {
        await createProduct(payload);
        toast.success('Producto creado');
      }
      setDrawerOpen(false);
      fetchProducts();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al guardar producto'));
    } finally {
      setIsLoading(false);
    }
  };

  const onDeactivate = async (id) => {
    if (!window.confirm('¿Desactivar este producto?')) return;
    try {
      await deleteProduct(id);
      toast.success('Producto desactivado');
      fetchProducts();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Error al desactivar producto'));
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#2B2D42] font-['Manrope']">Productos</h1>
          <p className="text-[#5D5C61] mt-1">Catalogo e inventario basico de productos</p>
        </div>
        <Button className="bg-[#335C67] hover:bg-[#244048] text-white rounded-full" onClick={() => openDrawer()}>
          <Plus className="w-4 h-4 mr-2" />
          Nuevo producto
        </Button>
      </div>

      <div className="grid gap-4">
        {products.map((p) => (
          <Card key={p.id} className="bg-white border-slate-100">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#335C67] flex items-center justify-center text-white">
                    <Package className="w-4 h-4" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{p.name}</CardTitle>
                    <CardDescription>
                      ${Number(p.sale_price || 0).toFixed(2)} · Stock: {p.stock}
                      {p.sku ? ` · SKU: ${p.sku}` : ''}
                    </CardDescription>
                  </div>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${p.is_active ? 'bg-[#588157]/10 text-[#588157]' : 'bg-slate-200 text-slate-600'}`}>
                  {p.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={() => openDrawer(p)}>
                  <Pencil className="w-4 h-4 mr-1" />
                  Editar
                </Button>
                {p.is_active && (
                  <Button variant="ghost" size="sm" className="text-[#9E2A2B] hover:bg-[#9E2A2B]/10" onClick={() => onDeactivate(p.id)}>
                    <Trash2 className="w-4 h-4 mr-1" />
                    Desactivar
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="text-2xl font-['Manrope']">{selectedProduct ? 'Editar producto' : 'Nuevo producto'}</SheetTitle>
            <SheetDescription>Gestiona la informacion comercial e inventario basico.</SheetDescription>
          </SheetHeader>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
            </div>
            <div className="space-y-2">
              <Label>Descripcion</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>SKU</Label>
              <Input value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Precio venta</Label>
                <Input type="number" step="0.01" min="0" value={formData.sale_price} onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Costo</Label>
                <Input type="number" step="0.01" min="0" value={formData.cost_price} onChange={(e) => setFormData({ ...formData, cost_price: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Stock actual</Label>
                <Input type="number" min="0" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} required />
              </div>
              <div className="space-y-2">
                <Label>Stock minimo</Label>
                <Input type="number" min="0" value={formData.min_stock} onChange={(e) => setFormData({ ...formData, min_stock: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Categoria</Label>
              <Input value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Imagen del producto</Label>
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              />
              {selectedProduct?.image_path && (
                <p className="text-xs text-[#8D99AE]">Imagen actual: {selectedProduct.image_path}</p>
              )}
            </div>
            <div className="flex items-center justify-between py-4 px-4 bg-[#F4F1EA] rounded-lg">
              <div>
                <Label className="font-medium">Producto activo</Label>
                <p className="text-sm text-[#5D5C61]">Disponible para nuevas ventas</p>
              </div>
              <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
            </div>

            <Button type="submit" className="w-full bg-[#335C67] hover:bg-[#244048] text-white rounded-full" disabled={isLoading}>
              {isLoading ? 'Guardando...' : 'Guardar'}
            </Button>
          </form>
        </SheetContent>
      </Sheet>
    </div>
  );
}

